import Swal from 'sweetalert2';
import { getApiErrorMessage } from '../helpers';
import { getCompanyDetails } from '../services/CompanyServices';
import { getAllProducts } from '../services/ProductServices';
import { getAllPlantConfigurations } from '../services/PlantConfigurationServices';

export function normalizeDcSelectInputs(inputs) {
  if (!inputs) return inputs;
  const idFields = [
    'production_plant_id',
    'production_grade_id',
    'purchase_grade_id',
    'received_grade_id',
    'other_grade_id',
    'plant_id',
    'work_schedule_id',
    'vehicle_id',
  ];
  const normalized = { ...inputs };
  idFields.forEach((key) => {
    const value = normalized[key];
    if (value !== undefined && value !== null && value !== '') {
      const num = Number(value);
      normalized[key] = Number.isNaN(num) ? value : num;
    }
  });
  return normalized;
}

export function mapFinishedGoods(productList) {
  return (productList.product_list || [])
    .filter((product) => product.status && product.category.name === 'FINISHED GOODS')
    .map((product) => ({ value: product.id, label: product.name }));
}

export function mapFilteredPlants(company) {
  return (company.plants || []).map((plant) => ({
    value: plant.id,
    label: plant.alias,
  }));
}

export function reloadDcFormLookups(cookies, draft, setters = {}) {
  const { setFilteredPlants, setFinishedGoods, setPlantConfig } = setters;
  const ws = draft?.selectedWorkSchedule;
  const tasks = [];

  if (setFinishedGoods && cookies?.myToken) {
    tasks.push(
      getAllProducts(cookies).then((productList) => {
        setFinishedGoods(mapFinishedGoods(productList));
      }),
    );
  }

  const companyId = ws?.sales_order_detail?.sales_order?.company?.id;
  if (setFilteredPlants && companyId && cookies?.myToken) {
    tasks.push(
      getCompanyDetails(cookies, companyId).then((company) => {
        setFilteredPlants(mapFilteredPlants(company));
      }),
    );
  }

  const plantId = ws?.plant?.id || draft?.inputs?.plant_id;
  if (setPlantConfig && plantId && cookies?.myToken) {
    tasks.push(
      getAllPlantConfigurations(cookies, plantId).then((plantConfigList) => {
        setPlantConfig(plantConfigList);
      }),
    );
  }

  return Promise.all(tasks).catch(() => {});
}

export const DC_FORM_RETURN_DRAFT_KEY = 'ksa:dcFormReturnDraft';

export function isVehicleExpiryError(message) {
  const text = String(message || '').toLowerCase();
  return text.includes("vehicle's") && text.includes('expired');
}

export function saveDcFormReturnDraft(returnPath, draft) {
  if (!returnPath) return;
  sessionStorage.setItem(
    DC_FORM_RETURN_DRAFT_KEY,
    JSON.stringify({
      returnPath,
      savedAt: Date.now(),
      ...draft,
    }),
  );
}

export function consumeDcFormReturnDraft(returnPath) {
  const raw = sessionStorage.getItem(DC_FORM_RETURN_DRAFT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed.returnPath !== returnPath) {
      return null;
    }
    sessionStorage.removeItem(DC_FORM_RETURN_DRAFT_KEY);
    return parsed;
  } catch {
    sessionStorage.removeItem(DC_FORM_RETURN_DRAFT_KEY);
    return null;
  }
}

export function openEquipmentFormFromDc({ navigate, returnPath, equipmentId, draft }) {
  if (!equipmentId || !navigate) return;
  if (draft) {
    saveDcFormReturnDraft(returnPath, draft);
  }
  navigate(`/EquipmentEdit/${equipmentId}`, { state: { returnTo: returnPath } });
}

export function tryHandleVehicleExpiryError(error, { navigate, returnPath, equipmentId, draft }) {
  const message = getApiErrorMessage(error);
  if (!isVehicleExpiryError(message) || !equipmentId || !navigate) {
    return false;
  }

  Swal.fire({
    title: 'Save Failed',
    html: `<p><i>${message}</i></p><p>Update Insurance, FC, and Permit due dates in the Equipment form, then return here to save the Delivery Challan.</p>`,
    icon: 'error',
    showCancelButton: true,
    confirmButtonText: 'Open Equipment Form',
    cancelButtonText: 'Close',
    customClass: { popup: 'swal-wide' },
  }).then((result) => {
    if (result.isConfirmed) {
      openEquipmentFormFromDc({ navigate, returnPath, equipmentId, draft });
    }
  });
  return true;
}

export function openJobCompletionFromDc({ navigate, returnPath, workScheduleId, draft }) {
  if (!workScheduleId || !navigate) return;
  if (draft) {
    saveDcFormReturnDraft(returnPath, draft);
  }
  navigate(`/JobCompletion/${workScheduleId}`, {
    state: { returnTo: returnPath || '/DeliveryChallan' },
  });
}

export function openSalesOrderFromDc({
  navigate,
  returnPath,
  salesOrderId,
  workScheduleId,
  draft,
}) {
  if (!salesOrderId || !navigate) return;
  if (draft) {
    saveDcFormReturnDraft(returnPath, {
      ...draft,
      pendingWorkScheduleId: workScheduleId || null,
    });
  }
  navigate(`/SalesOrderEdit/${salesOrderId}`, {
    state: {
      returnTo: returnPath || '/DeliveryChallan',
      nextWorkScheduleId: workScheduleId || null,
    },
  });
}

export function openWorkScheduleFromDc({ navigate, returnPath, workScheduleId, draft }) {
  if (!workScheduleId || !navigate) return;
  if (draft) {
    saveDcFormReturnDraft(returnPath, draft);
  }
  navigate(`/WorkScheduleEdit/${workScheduleId}`, {
    state: { returnTo: returnPath || '/DeliveryChallan' },
  });
}

export function showBlockingWorkScheduleAlert(workscheduleList, { navigate, returnPath, draft }) {
  const message = workscheduleList?.message;
  if (!message) return false;

  const wsId = workscheduleList.blocking_work_schedule_id;
  const scheduleNo = workscheduleList.blocking_schedule_no;
  const soId = workscheduleList.blocking_sales_order_id;
  const soNo = workscheduleList.blocking_sales_order_no;
  const path = returnPath || '/DeliveryChallan';

  const openJobCompletion = () => {
    if (!wsId) return;
    openJobCompletionFromDc({
      navigate,
      returnPath: path,
      workScheduleId: wsId,
      draft,
    });
  };

  const openSalesOrder = () => {
    if (!soId) return;
    openSalesOrderFromDc({
      navigate,
      returnPath: path,
      salesOrderId: soId,
      workScheduleId: wsId,
      draft,
    });
  };

  let linkedMessage = String(message);
  if (wsId) {
    linkedMessage = linkedMessage.replace(
      /Workschedule no\s+(\d+)/i,
      `<a href="#" id="dc-open-blocking-ws" style="color:#5b2be0;font-weight:700;text-decoration:underline;">Workschedule no $1</a>`,
    );
  }

  const soLinkHtml = soId
    ? `<p style="margin-top:8px;">To extend validity, open Sales Order <a href="#" id="dc-open-blocking-so" style="color:#5b2be0;font-weight:700;text-decoration:underline;">${soNo || soId}</a>, update <b>Validity Date</b>, and related open work schedules will update automatically.</p>`
    : '';

  Swal.fire({
    icon: 'info',
    html: `
      <div style="text-align:left;line-height:1.45;">
        <p>${linkedMessage}</p>
        ${wsId ? `<p style="margin-top:10px;">Click <b>Workschedule no ${scheduleNo || wsId}</b> (or Open Job Completion) to complete it.</p>` : ''}
        ${soLinkHtml}
      </div>
    `,
    showCancelButton: Boolean(wsId || soId),
    showDenyButton: Boolean(soId),
    confirmButtonText: wsId ? 'Open Job Completion' : 'OK',
    denyButtonText: 'Extend Validity (Sales Order)',
    cancelButtonText: 'Close',
    customClass: { popup: 'swal-wide' },
    didOpen: () => {
      document.getElementById('dc-open-blocking-ws')?.addEventListener('click', (event) => {
        event.preventDefault();
        Swal.close();
        openJobCompletion();
      });
      document.getElementById('dc-open-blocking-so')?.addEventListener('click', (event) => {
        event.preventDefault();
        Swal.close();
        openSalesOrder();
      });
    },
  }).then((result) => {
    if (result.isConfirmed && wsId) {
      openJobCompletion();
    } else if (result.isDenied && soId) {
      openSalesOrder();
    }
  });
  return true;
}

export function restoreDcFormDraftIfReturned(location, returnPath, applyDraft) {
  const fromEquipment = Boolean(location.state?.fromEquipment);
  const fromCheckIn = Boolean(location.state?.fromCheckIn);
  const fromCheckInCancel = Boolean(location.state?.fromCheckInCancel);
  const fromJobCompletion = Boolean(location.state?.fromJobCompletion);
  const fromSalesOrder = Boolean(location.state?.fromSalesOrder);
  const fromWorkSchedule = Boolean(location.state?.fromWorkSchedule);
  if (
    !fromEquipment
    && !fromCheckIn
    && !fromCheckInCancel
    && !fromJobCompletion
    && !fromSalesOrder
    && !fromWorkSchedule
  ) {
    return false;
  }
  const draft = consumeDcFormReturnDraft(returnPath);
  if (!draft) {
    return false;
  }
  applyDraft(draft);
  if (fromCheckIn) {
    Swal.fire('Check-in completed. You can continue with the delivery challan.', '', 'success');
  } else if (fromEquipment) {
    Swal.fire('Equipment updated. Please save the delivery challan again.', '', 'success');
  } else if (fromJobCompletion) {
    Swal.fire('Work schedule updated. You can continue with the delivery challan.', '', 'success');
  } else if (fromSalesOrder || fromWorkSchedule) {
    Swal.fire('Validity updated. You can continue with the delivery challan.', '', 'success');
  }
  return true;
}
